import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Plus, X, ChevronDown, Pencil, Play, Square } from 'lucide-react';
import type { Task } from '@/types';
import { getInitials, getAvatarColor } from '@/utils/formatters';
import { useProfilesContext } from '@/context/ProfilesContext';
import { useAuth } from '@/context/AuthContext';
import { parseDuration, formatDuration } from '@/utils/duration';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useClickOutside } from '@/hooks/useClickOutside';
import { fetchTimeEntries, addTimeEntry, deleteTimeEntry, type TimeEntry } from '@/db/timeEntries';
import { InlineDatePicker } from '@/components/shared/inline-editors';
import { useTimerStore } from '@/store/timerStore';
import clsx from 'clsx';

interface TimeTrackingEditorProps {
  task: Task;
  onUpdate: (updates: Partial<Task>) => void;
  size?: 'sm' | 'md';
  disabled?: boolean;
}

export function TimeTrackingEditor({
  task,
  onUpdate,
  size = 'md',
  disabled = false,
}: TimeTrackingEditorProps) {
  const { resolveDisplayName } = useProfilesContext();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Estimate editing
  const [isEditingEstimate, setIsEditingEstimate] = useState(false);
  const [estimateInput, setEstimateInput] = useState('');

  // Time entries
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  // Add entry form
  const [isAddingEntry, setIsAddingEntry] = useState(false);
  const [newEntryDate, setNewEntryDate] = useState<number | null>(() => Date.now());
  const [newEntryDuration, setNewEntryDuration] = useState('');

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const estimateInputRef = useRef<HTMLInputElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 8,
    minWidth: 340,
    matchTriggerWidth: false,
  });

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsEditingEstimate(false);
    setIsAddingEntry(false);
  }, []);

  useClickOutside([dropdownRef, triggerRef], handleClose, isOpen);

  // Fetch time entries when dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingEntries(true);
    fetchTimeEntries({
      taskId: task.id,
      startDate: '2000-01-01',
      endDate: '2099-12-31',
    })
      .then((data) => {
        if (!cancelled) {
          // Sort descending by date
          setEntries(data.sort((a, b) => b.date.localeCompare(a.date)));
        }
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingEntries(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, task.id]);

  // Focus estimate input when entering edit mode
  useEffect(() => {
    if (isEditingEstimate && estimateInputRef.current) {
      estimateInputRef.current.focus();
      estimateInputRef.current.select();
    }
  }, [isEditingEstimate]);

  // Derived values
  const estimate = task.timeEstimated ?? 0;
  const totalTracked = entries.reduce((sum, e) => sum + e.duration, 0);
  const percentage = estimate > 0 ? Math.round((totalTracked / estimate) * 100) : 0;

  // Estimate handlers
  const handleStartEditEstimate = () => {
    setEstimateInput(estimate > 0 ? formatDuration(estimate) : '');
    setIsEditingEstimate(true);
  };

  const handleSaveEstimate = () => {
    const parsed = parseDuration(estimateInput);
    if (parsed !== null && parsed > 0) {
      onUpdate({ timeEstimated: parsed });
    } else if (estimateInput.trim() === '') {
      onUpdate({ timeEstimated: null });
    }
    setIsEditingEstimate(false);
  };

  const handleEstimateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEstimate();
    } else if (e.key === 'Escape') {
      setIsEditingEstimate(false);
    }
  };

  // Add time entry handler
  const handleAddEntry = async () => {
    if (!user) return;
    const parsed = parseDuration(newEntryDuration);
    if (parsed === null || parsed <= 0) return;

    try {
      const entry = await addTimeEntry({
        taskId: task.id,
        userId: user.id,
        date: newEntryDate ? format(new Date(newEntryDate), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        duration: parsed,
      });
      setEntries((prev) =>
        [entry, ...prev.filter((e) => e.id !== entry.id)].sort((a, b) =>
          b.date.localeCompare(a.date)
        )
      );
      setIsAddingEntry(false);
      setNewEntryDuration('');
      setNewEntryDate(Date.now());
    } catch {
      // silently fail — could add toast later
    }
  };

  // Delete time entry handler
  const handleDeleteEntry = async (entryId: string) => {
    try {
      await deleteTimeEntry(entryId);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
    } catch {
      // silently fail
    }
  };

  // Progress bar color
  const getProgressColor = () => {
    if (percentage > 100) return 'bg-accent-rose';
    if (percentage >= 80) return 'bg-amber-500';
    return 'bg-accent-green';
  };

  const displayTotal = estimate > 0 ? estimate : 0;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        className={clsx(
          'flex items-center gap-1.5 rounded-md transition-all bg-surface-tertiary',
          size === 'sm' ? 'px-1.5 h-6 text-[10px]' : 'px-2 h-7 text-[11px]',
          !disabled && 'hover:bg-surface-hover cursor-pointer',
          disabled && 'cursor-default opacity-70',
          displayTotal ? 'text-text-secondary' : 'text-text-quaternary'
        )}
        title={disabled ? undefined : 'Gestisci stime e tempo tracciato'}
      >
        <Clock className={clsx(size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5')} />
        <span className="truncate">{displayTotal ? formatDuration(displayTotal) : 'Stima'}</span>
        {!disabled && (
          <ChevronDown
            className={clsx(
              'w-3 h-3 text-text-quaternary shrink-0 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        )}
      </button>

      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="rounded-lg shadow-2xl animate-fade-in border border-border-primary/70 bg-surface-primary z-50 max-h-[500px] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Section 1: Stima */}
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-text-quaternary" />
                  <span className="text-[10px] font-semibold text-text-quaternary uppercase tracking-wider">
                    Stima
                  </span>
                </div>
                {isEditingEstimate ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      ref={estimateInputRef}
                      type="text"
                      value={estimateInput}
                      onChange={(e) => setEstimateInput(e.target.value)}
                      onKeyDown={handleEstimateKeyDown}
                      onBlur={handleSaveEstimate}
                      placeholder="2h 30m"
                      className="w-24 px-2 py-1 text-[11px] bg-surface-secondary border border-accent-violet rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet text-text-primary placeholder:text-text-quaternary text-right"
                    />
                  </div>
                ) : (
                  <button
                    onClick={handleStartEditEstimate}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-surface-hover transition-colors group cursor-pointer"
                  >
                    <span className="text-[11px] font-medium text-text-primary">
                      {estimate > 0 ? formatDuration(estimate) : '—'}
                    </span>
                    <Pencil className="w-3 h-3 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-border-primary/50" />

            {/* Section 2: Tracked Time Summary + Progress */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-text-quaternary uppercase tracking-wider">
                  Tracciato
                </span>
                <span className="text-[11px] font-medium text-text-primary">
                  {totalTracked > 0 ? formatDuration(totalTracked) : '0m'}
                </span>
              </div>

              {/* Progress bar (only show when estimate > 0) */}
              {estimate > 0 && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full transition-all', getProgressColor())}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>
                  <span
                    className={clsx(
                      'text-[10px] font-medium tabular-nums',
                      percentage > 100
                        ? 'text-accent-rose'
                        : percentage >= 80
                          ? 'text-amber-500'
                          : 'text-text-tertiary'
                    )}
                  >
                    {percentage}%
                  </span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-border-primary/50" />

            {/* Section 3: Time Entries List */}
            <div className="px-4 py-3">
              {loadingEntries ? (
                <div className="text-[10px] text-text-quaternary text-center py-2">
                  Caricamento...
                </div>
              ) : entries.length === 0 ? (
                <div className="text-[10px] text-text-quaternary text-center py-2">
                  Nessun tempo tracciato
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {entries.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-surface-tertiary/50 group"
                    >
                      <span className="text-[10px] text-text-tertiary shrink-0 w-12">
                        {format(new Date(entry.date + 'T00:00:00'), 'd MMM', { locale: it })}
                      </span>
                      <div
                        className={clsx(
                          'w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white shrink-0',
                          getAvatarColor(resolveDisplayName(entry.userId))
                        )}
                      >
                        {getInitials(resolveDisplayName(entry.userId))}
                      </div>
                      <span className="text-[10px] text-text-secondary truncate flex-1 min-w-0">
                        {resolveDisplayName(entry.userId)}
                      </span>
                      <span className="text-[11px] font-medium text-text-primary tabular-nums shrink-0">
                        {formatDuration(entry.duration)}
                      </span>
                      <button
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="p-0.5 rounded hover:bg-surface-hover transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                        title="Elimina"
                      >
                        <X className="w-3 h-3 text-text-quaternary hover:text-accent-rose" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-border-primary/50" />

            {/* Section 4: Add Entry */}
            <div className="px-4 py-3">
              {isAddingEntry ? (
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <InlineDatePicker
                        value={newEntryDate}
                        onChange={setNewEntryDate}
                        showTime={false}
                        size="sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={newEntryDuration}
                      onChange={(e) => setNewEntryDuration(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddEntry();
                        if (e.key === 'Escape') setIsAddingEntry(false);
                      }}
                      placeholder="1h 30m"
                      className="w-24 px-2 py-1.5 text-[11px] bg-surface-secondary border border-border-primary rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet text-text-primary placeholder:text-text-quaternary"
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleAddEntry}
                      disabled={!newEntryDuration.trim()}
                      className="flex-1 px-3 py-1.5 text-[11px] font-medium text-white bg-accent-violet rounded-md hover:bg-accent-violet/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Salva
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingEntry(false);
                        setNewEntryDuration('');
                      }}
                      className="px-3 py-1.5 text-[11px] font-medium text-text-secondary bg-surface-secondary rounded-md hover:bg-surface-hover transition-colors"
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsAddingEntry(true)}
                    className="flex items-center gap-1.5 text-[11px] font-medium text-accent-violet hover:text-accent-violet/80 transition-colors flex-1 justify-center py-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Aggiungi tempo
                  </button>
                  <TimerButton taskId={task.id} taskName={task.name} />
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function TimerButton({ taskId, taskName }: { taskId: string; taskName: string }) {
  const { activeTaskId, isRunning, startTimer, stopTimer } = useTimerStore();
  const { user } = useAuth();
  const isThisTask = activeTaskId === taskId;

  if (isThisTask && isRunning) {
    return (
      <button
        onClick={async () => { if (user) await stopTimer(user.id); }}
        className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-accent-rose bg-accent-rose/10 hover:bg-accent-rose/20 rounded-md transition-colors"
        title="Ferma timer e salva"
      >
        <Square className="w-3 h-3" />
        Stop
      </button>
    );
  }

  return (
    <button
      onClick={() => startTimer(taskId, taskName)}
      className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-accent-emerald bg-accent-emerald/10 hover:bg-accent-emerald/20 rounded-md transition-colors"
      title="Avvia timer per questo task"
    >
      <Play className="w-3 h-3" />
      Timer
    </button>
  );
}
